import { useContext, useEffect, useState } from 'react'
import { Column } from 'react-table'
import Table from '../../../global/components/table/Table'
import { IAudit } from '../../../../../app/entities/Audit'
import { handleColumnFilterUpdate } from '../../../global/components/table/helpers/multiColumnFilters'
import retrieveData from '../../../global/components/table/helpers/retrieveData'
import BasicFilter from '../../../global/components/table/helpers/BasicFilter'
import AssignUserCell from '../../../global/components/assign-user/AssignUserCell'
import { PERMISSIONS } from '../../../global/constants/permissions'
import hasPermission from '../../../global/utils/user/has-permission'
import { UserContext } from '../../../base/components/base-container/BaseContainer'
import { fetchData } from '../../../global/utils/fetch'
import { assignUserToCoaching, getAuditAssignToUserList } from '../api'
import { IUser } from '../../../../../app/entities/User'
import StartCell from '../common/start/Start'
import NoFilter from '../../../global/components/table/helpers/NoFilter'
import { ILocationFilters } from '../../main/AuditsMain'
import { formatDate } from '../../../global/utils/date/mm-dd-yyyy'
import CoachingModal from '../coaching-modal/CoachingModal'

async function fetchAssignToUserList(setUserList: Function) {
  const users = await fetchData<IUser[]>(
    getAuditAssignToUserList(PERMISSIONS.AUDITS.ENTRIES.COACHING_ASSIGNEE.NAME),
  )
  setUserList(users)
}

interface ICoachincAuditsTableProps {
  setRefetchIndex: Function
  refetchIndex: number
  locationFilters: ILocationFilters
}

function CoachingAuditsTable(props: ICoachincAuditsTableProps) {
  const { setRefetchIndex, refetchIndex, locationFilters } = props
  const { user } = useContext(UserContext)
  const [usersList, setUsersList] = useState<IUser[]>([])
  const [coachingModalOpen, setCoachingModalOpen] = useState(false)
  const [tableState, setTableState] = useState({
    data: [],
    isLoading: false,
    offSet: 0,
    pageSize: 100,
    count: 0,
  })
  const [columnFilters, setColumnFilters] = useState({})
  const coachingTableEndpoint = `${process.env.REACT_APP_ORIGIN}/audit/coaching?`

  function onColumnFilterUpdate(filtersUpdate: object) {
    handleColumnFilterUpdate(filtersUpdate, columnFilters, setColumnFilters)
  }

  function retrieveTableData() {
    retrieveData(setTableState, {
      endpoint: coachingTableEndpoint,
      pageSize: tableState.pageSize,
      offSet: tableState.offSet,
      columnFilters,
      selectedLocationId: locationFilters.locationFilter,
    })
  }

  useEffect(() => {
    retrieveTableData()
  }, [refetchIndex, locationFilters, tableState.pageSize, tableState.offSet])

  useEffect(() => {
    fetchAssignToUserList(setUsersList)
  }, [])

  const columns: Column<IAudit>[] = [
    {
      Header: 'Assigned To',
      accessor: 'assignedToCoach',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ assignedToCoach: val }),
        onEnter: retrieveTableData,
      }),
      Cell: ({ row: { original } }) => {
        const { assignedToCoach, objectId } = original as any
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <AssignUserCell
              assignedTo={assignedToCoach}
              usersList={usersList}
              objectId={objectId}
              retrieveTableData={retrieveTableData}
              disabled={
                !hasPermission(user).allow(
                  PERMISSIONS.AUDITS.CATEGORY,
                  PERMISSIONS.AUDITS.ENTRIES.COACHING_ASSIGNER.NAME,
                )
              }
              assignUserTo={assignUserToCoaching}
            />
          </div>
        )
      },
    },
    {
      Header: 'Technician',
      accessor: ({ technician }: IAudit) => technician?.name,
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ technician: { name: val } }),
        onEnter: retrieveTableData,
      }),
    },
    {
      Header: 'Tech Location',
      accessor: ({ technician }: IAudit) => technician?.location?.name,
      Filter: NoFilter(),
    },
    {
      Header: 'Operation Date',
      accessor: 'operationDate',
      Filter: NoFilter(),
      Cell: ({ row: { original } }) => {
        const date = new Date(original.operationDate)
        const formattedDate = formatDate(date)
        return <>{formattedDate}</>
      },
    },
    {
      Header: 'Status',
      accessor: 'status',
      Filter: BasicFilter({
        onChange: (val: string) => onColumnFilterUpdate({ status: val }),
        onEnter: retrieveTableData,
      }),
    },
    {
      Header: '',
      accessor: 'progress',
      Filter: NoFilter(),
      Cell: ({ row: { original } }) => {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <StartCell
              // This is the component from within which we handle the data query
              // and modal opening
              audit={original}
              setModalOpen={setCoachingModalOpen}
              disabled={
                !hasPermission(user).allow(
                  PERMISSIONS.AUDITS.CATEGORY,
                  PERMISSIONS.AUDITS.ENTRIES.PERFORM_COACHING.NAME,
                )
              }
              onClickAction={async () => {
                await fetchData(
                  assignUserToCoaching(original.objectId, user?.name || ''),
                )
              }}
            />
          </div>
        )
      },
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <CoachingModal
        isOpen={coachingModalOpen}
        setIsOpen={setCoachingModalOpen}
        refetchIndex={refetchIndex}
        setRefetchIndex={setRefetchIndex}
      />
      <Table
        columns={columns}
        data={tableState.data ?? []}
        onRowClick={() => {}}
        isLoading={tableState.isLoading}
        isPaginated
        pagination={{ setTableState, tableState }}
        isTableTopperPresent={false}
      />
    </div>
  )
}

export default CoachingAuditsTable
